import { useState, useEffect } from 'preact/hooks'
import { X } from 'lucide-preact'
import { TypeAheadDropdown, type TypeAheadOption } from './TypeAheadDropdown'
import { PersonAvatar } from './Avatar'
import { workflowSettingsApi, type WorkflowSettingsUserDto, type EligibleUserDto } from '@/api/workflowSettings'
import { useToast } from '@/hooks/useToast'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

export interface UserPickerProps {
  /** Field label */
  label?: string
  /** Whether field is required */
  required?: boolean
  /** Field name */
  name?: string
  /** Placeholder text for the search input */
  placeholder?: string
  /** Error message to display */
  error?: string
  /** Helper text to display below the component */
  helperText?: string
  /** Whether the picker is disabled */
  disabled?: boolean
  /** Custom class for container */
  containerClassName?: string
  /** Workspace ID for user search */
  workspaceId: string
  /** Client company ID for user search */
  clientCompanyId: string
  /** Type of user to search for */
  userType: 'reviewer' | 'internal'
  /** Currently selected users */
  selectedUsers: WorkflowSettingsUserDto[]
  /** Handler when users are added or removed */
  onChange: (users: WorkflowSettingsUserDto[]) => void
}

/**
 * UserPicker - A specialized type-ahead component for selecting workflow users
 *
 * Features:
 * - Type-ahead search with debouncing
 * - Shows user avatars in search results and selected chips
 * - Prevents duplicate selections
 * - Displays selected users as removable chips
 * - Integrates with workflow settings API
 *
 * @example
 * ```tsx
 * const [reviewers, setReviewers] = useState<WorkflowSettingsUserDto[]>([])
 *
 * <UserPicker
 *   label="Default Reviewers"
 *   placeholder="Search for reviewers..."
 *   workspaceId={workspaceId}
 *   clientCompanyId={clientCompanyId}
 *   userType="reviewer"
 *   selectedUsers={reviewers}
 *   onChange={setReviewers}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With React Hook Form Controller
 * <Controller
 *   name="defaultReviewerUsers"
 *   control={control}
 *   render={({ field, fieldState }) => (
 *     <UserPicker
 *       label="Default Reviewers"
 *       workspaceId={workspaceId}
 *       clientCompanyId={clientCompanyId}
 *       userType="reviewer"
 *       selectedUsers={field.value || []}
 *       onChange={field.onChange}
 *       error={fieldState.error?.message}
 *     />
 *   )}
 * />
 * ```
 */
export function UserPicker({
  label,
  required = false,
  name,
  placeholder = 'Type to search users...',
  error,
  helperText,
  disabled = false,
  containerClassName = '',
  workspaceId,
  clientCompanyId,
  userType,
  selectedUsers,
  onChange
}: UserPickerProps) {
  const { showError, showErrorUnless422 } = useToast()
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<EligibleUserDto[]>([])
  const debouncedSearchValue = useDebouncedValue(searchValue, 300)

  // Search for users when debounced search value changes
  useEffect(() => {
    if (debouncedSearchValue.length < 3) {
      setSearchResults([])
      setLoading(false)
      return
    }

    const abortController = new AbortController()
    setLoading(true)

    workflowSettingsApi
      .searchEligibleUsers(workspaceId, clientCompanyId, debouncedSearchValue, userType)
      .then((response) => {
        if (abortController.signal.aborted) return

        if (response.error) {
          showErrorUnless422('Search failed', response)
          setSearchResults([])
          return
        }

        if (response.data) {
          const selectedIds = new Set(selectedUsers.map(u => u.Id))
          const filtered = response.data.filter(user => !selectedIds.has(user.Id))
          setSearchResults(filtered)
        }
      })
      .catch(() => {
        if (abortController.signal.aborted) return
        showError('Search failed', 'Failed to search users')
        setSearchResults([])
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      })

    return () => {
      abortController.abort()
    }
  }, [debouncedSearchValue, workspaceId, clientCompanyId, userType, selectedUsers, showError, showErrorUnless422])

  const handleUserSelect = (option: TypeAheadOption<EligibleUserDto>) => {
    const user = option.data
    const newUser: WorkflowSettingsUserDto = {
      Id: user.Id,
      FirstName: user.FirstName,
      LastName: user.LastName,
      AvatarUrl: user.AvatarUrl
    }

    // Add to selected users
    onChange([...selectedUsers, newUser])

    // Clear search
    setSearchValue('')
    setSearchResults([])
  }

  const handleRemoveUser = (userId: string) => {
    onChange(selectedUsers.filter(u => u.Id !== userId))
  }

  const options: TypeAheadOption<EligibleUserDto>[] = searchResults.map(user => ({
    key: user.Id,
    label: `${user.FirstName} ${user.LastName}`,
    data: user,
    render: () => (
      <div className="flex items-center gap-3">
        <PersonAvatar
          firstName={user.FirstName}
          lastName={user.LastName}
          profilePicUrl={user.AvatarUrl}
          size="sm"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 dark:text-white">
            {user.FirstName} {user.LastName}
          </div>
          {user.Email && (
            <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {user.Email}
            </div>
          )}
        </div>
      </div>
    )
  }))

  return (
    <div className={containerClassName}>
      <TypeAheadDropdown
        label={label}
        required={required}
        name={name}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        disabled={disabled}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        loading={loading}
        options={options}
        onSelect={handleUserSelect}
        onClear={() => setSearchValue('')}
        showClearButton={true}
        noResultsMessage="No users found"
        minLengthMessage={`Type at least 3 characters to search for ${userType === 'reviewer' ? 'reviewers' : 'internal team members'}`}
      />

      {/* Selected Users Chips */}
      {selectedUsers.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selectedUsers.map(user => (
            <div
              key={user.Id}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 rounded-full border border-slate-200 dark:border-slate-600 group hover:border-slate-300 dark:hover:border-slate-500 transition-colors"
            >
              <PersonAvatar
                firstName={user.FirstName}
                lastName={user.LastName}
                profilePicUrl={user.AvatarUrl}
                size="sm"
              />
              <span className="text-sm font-medium text-slate-900 dark:text-white">
                {user.FirstName} {user.LastName}
              </span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveUser(user.Id)}
                  className="ml-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
                  aria-label={`Remove ${user.FirstName} ${user.LastName}`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
